import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Geolocation } from '@capacitor/geolocation';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Navigation, Search, ZoomIn, ZoomOut, Loader2, MapPin, Home, Briefcase, X, ArrowLeft, LocateOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { LocationStatus, locateCurrentPosition, locationNotice } from '../../utils/currentLocation';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small native plugin generated into the Android app by scripts/build_app.py:
// opens this app's settings page (to re-allow a blocked permission) or the
// device's location settings. Absent on iOS, the web, and older app builds —
// always check Capacitor.isPluginAvailable('AppSettings') before calling.
const AppSettings = registerPlugin('AppSettings');

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const ADDRESS_TYPES = [
  { value: 'Home', icon: Home, label: 'Home' },
  { value: 'Work', icon: Briefcase, label: 'Work' },
  { value: 'Other', icon: MapPin, label: 'Other' },
];

const DEFAULT_CENTER = [20.5937, 78.9629];
const DEFAULT_ZOOM = 5;

const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({ click(e) { onLocationSelect(e.latlng.lat, e.latlng.lng); } });
  return null;
};

const FlyTo = ({ position, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, zoom || 17, { duration: 1.2 });
  }, [position, zoom, map]);
  return null;
};

/* Exports the Leaflet map instance to parent state so controls outside MapContainer can call map methods */
const MapRefExporter = ({ onMap }) => {
  const map = useMap();
  useEffect(() => { onMap(map); }, [map, onMap]);
  return null;
};

// locationOnly: skips the address-detail form entirely — "Confirm Location"
// calls onSave directly with just { full_address, latitude, longitude }. Used
// by the admin apartment picker, which only needs a single common map pin,
// not the full customer address breakdown (landmark/city/state/apartment).
// Defaults to false, so every existing call site (customer address flows) is
// completely unaffected.
const MapAddressPicker = ({ initialAddress, onSave, onClose, isEditing, locationOnly = false }) => {
  const { storeSettings } = useAuth();
  const isApartmentsEnabled = storeSettings?.tenant_features?.serviceable_apartments === true;
  const [apartments, setApartments] = useState([]);

  // Whether we need to know the apartment list BEFORE deciding which screen to
  // show first — only true for a brand-new address (not editing, not the
  // admin's locationOnly pin picker) on a tenant with the feature on. Editing
  // an address always keeps its previous entry point (see step init below),
  // and locationOnly never shows apartment selection at all.
  const needsApartmentCheck = !locationOnly && !initialAddress && isApartmentsEnabled;
  const [apartmentsLoaded, setApartmentsLoaded] = useState(!needsApartmentCheck);

  useEffect(() => {
    if (!isApartmentsEnabled || locationOnly) return;
    axios.get(`${API}/admin/serviceable-apartments`)
      .then(res => setApartments(res.data || []))
      .catch(() => {})
      .finally(() => setApartmentsLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApartmentsEnabled, locationOnly]);

  // Gating is only actually active once the tenant feature is on AND the admin
  // has configured at least one apartment — enabled-but-unconfigured must behave
  // exactly like a tenant that doesn't use this feature at all (no blocking, no
  // required field), mirroring the backend's _validate_and_enrich_apartment.
  const apartmentGatingActive = isApartmentsEnabled && apartments.length > 0;

  // Scenario 1 (enabled + configured): a brand-new address skips the map
  // entirely and starts on the apartment picker instead. Scenario 2 (enabled,
  // nothing configured yet) and Scenario 3 (disabled) fall through to the
  // existing map-first flow, completely unchanged.
  const [step, setStep] = useState(() => {
    if (locationOnly) return 'map';
    if (initialAddress?.apartment_id) return 'form'; // editing a known apartment address — location's already known
    if (needsApartmentCheck) return 'resolving'; // wait for the apartments fetch before picking a screen
    return 'map';
  });
  // True once the form step should render the simplified apartment-only
  // fields (Address Type + selected apartment + flat number) instead of the
  // full map-derived address form. Set on mount when editing a known
  // apartment address, or when the customer picks one from the new list step.
  const [apartmentFormMode, setApartmentFormMode] = useState(!!initialAddress?.apartment_id);
  const [apartmentSearchQuery, setApartmentSearchQuery] = useState('');
  // "Other" (tenant-configurable): the customer opts out of apartment-based
  // delivery entirely and falls back to the original map-based flow below,
  // unmodified. Also defaults on when editing an address that was never tied
  // to an apartment — it stays on the original flow it was created with,
  // instead of suddenly being forced to pick an apartment on every edit.
  const isOtherAddressEnabled = storeSettings?.serviceable_apartments_other_enabled === true;
  const [otherAddressMode, setOtherAddressMode] = useState(
    !locationOnly && !!initialAddress && !initialAddress?.apartment_id
  );

  // Once the apartments fetch resolves, send a fresh address into the
  // apartment-first flow only if the tenant actually has apartments configured.
  useEffect(() => {
    if (step !== 'resolving' || !apartmentsLoaded) return;
    setStep(apartments.length > 0 ? 'apartment' : 'map');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, apartmentsLoaded]);

  const selectApartment = (apartment) => {
    setForm(prev => ({
      ...prev,
      apartment_id: apartment.id,
      apartment_name: apartment.name,
      full_address: `${apartment.name}${apartment.address ? ', ' + apartment.address : ''}`,
      pincode: apartment.pincode || '',
      latitude: apartment.latitude ?? null,
      longitude: apartment.longitude ?? null,
    }));
    setApartmentFormMode(true);
    setOtherAddressMode(false);
    setStep('form');
  };

  // "Other": leave the apartment flow entirely and continue with the
  // original map-based address flow, unmodified — see otherAddressMode above.
  // Also clears apartmentFormMode/apartment_id in case this was reached via
  // "Change" from an in-progress apartment address (add or edit) — otherwise
  // the form step would still render its simplified apartment-only variant
  // even though the customer just opted out of it.
  const selectOther = () => {
    setOtherAddressMode(true);
    setApartmentFormMode(false);
    setForm(prev => ({ ...prev, apartment_id: null, apartment_name: '' }));
    setStep('map');
  };

  const [mapRef, setMapRef] = useState(null);

  // Leaflet measures its tile grid from the container's size at the moment it
  // mounts. Here that container can mount mid-transition — e.g. the admin
  // flow closes a Dialog and opens this full-screen map overlay in the same
  // tick, so the map can measure itself before the Dialog's close animation
  // (and the resulting reflow) has actually finished, leaving it undersized/
  // partially blank, especially on mobile. Re-measuring shortly after mount,
  // and again on any viewport resize/orientation change, fixes both.
  useEffect(() => {
    if (!mapRef || step !== 'map') return;
    const invalidate = () => mapRef.invalidateSize();
    const t1 = setTimeout(invalidate, 100);
    const t2 = setTimeout(invalidate, 400);
    window.addEventListener('resize', invalidate);
    return () => { clearTimeout(t1); clearTimeout(t2); window.removeEventListener('resize', invalidate); };
  }, [mapRef, step]);

  const [position, setPosition] = useState(
    initialAddress?.latitude && initialAddress?.longitude
      ? [initialAddress.latitude, initialAddress.longitude]
      : null
  );
  const [form, setForm] = useState({
    label: initialAddress?.label || 'Home',
    full_address: initialAddress?.full_address || '',
    landmark: initialAddress?.landmark || '',
    pincode: initialAddress?.pincode || '',
    city: initialAddress?.city || '',
    state: initialAddress?.state || '',
    latitude: initialAddress?.latitude || null,
    longitude: initialAddress?.longitude || null,
    apartment_id: initialAddress?.apartment_id || null,
    apartment_name: initialAddress?.apartment_name || '',
    flat_number: initialAddress?.flat_number || '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [locating, setLocating] = useState(false);
  const [reversing, setReversing] = useState(false);
  const searchTimeout = useRef(null);

  // Apartments left without a pincode by the admin are treated as serviceable
  // for any PIN (pincode is an optional narrowing filter) — mirrors the
  // backend's matching rule exactly.
  const matchingApartments = apartmentGatingActive
    ? apartments.filter(a => !a.pincode || a.pincode === form.pincode)
    : [];
  const pincodeServiceable = !apartmentGatingActive || matchingApartments.length > 0;

  // If the pincode changes such that the previously-selected apartment no
  // longer applies, clear the stale selection so the customer must re-pick.
  useEffect(() => {
    if (!apartmentGatingActive || !form.apartment_id) return;
    if (!matchingApartments.some(a => a.id === form.apartment_id)) {
      setForm(p => ({ ...p, apartment_id: null, flat_number: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pincode, apartments]);

  // Reverse geocoding is async and can resolve out of order (a slow response
  // from an earlier map tap can land after a faster one from a later tap) or
  // arrive after the user has already moved to the form step and started
  // typing — without guarding against both, a stale/late response can silently
  // clobber a field the customer deliberately edited (e.g. correcting the
  // pincode to their actual registered PIN when OSM's reverse-geocode snaps to
  // a neighboring one). geocodeSeqRef ignores stale responses; userEditedRef
  // protects any field the customer has touched by hand from ever being
  // overwritten by a geocode result, until they pick a new location.
  const geocodeSeqRef = useRef(0);
  const userEditedRef = useRef(new Set());

  const reverseGeocode = useCallback(async (lat, lng) => {
    const seq = ++geocodeSeqRef.current;
    setReversing(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&accept-language=en`,
        { headers: { 'User-Agent': 'GroceryApp/1.0' } }
      );
      const data = await res.json();
      if (data?.address && seq === geocodeSeqRef.current) {
        const a = data.address;
        const edited = userEditedRef.current;
        setForm(prev => ({
          ...prev,
          full_address: edited.has('full_address') ? prev.full_address : (data.display_name || ''),
          city: edited.has('city') ? prev.city : (a.city || a.town || a.village || a.county || ''),
          state: edited.has('state') ? prev.state : (a.state || ''),
          pincode: edited.has('pincode') ? prev.pincode : (a.postcode || ''),
          latitude: lat,
          longitude: lng,
        }));
      }
    } catch { /* user can fill manually */ }
    finally { setReversing(false); }
  }, []);

  // Bumped on every picked location (map tap, search result, GPS) so a slow
  // GPS fix can't move the pin after the customer has already chosen a spot.
  const pickSeqRef = useRef(0);

  const handleLocationSelect = useCallback((lat, lng) => {
    pickSeqRef.current += 1;
    userEditedRef.current.clear(); // a newly picked location intentionally re-populates every field
    setPosition([lat, lng]);
    setForm(prev => ({ ...prev, latitude: lat, longitude: lng }));
    reverseGeocode(lat, lng);
  }, [reverseGeocode]);

  // Why the current location couldn't be used ({ status, interactive }), shown
  // as one inline notice — not a toast, so a refused permission can't stack up
  // repeated errors. Search and map taps keep working underneath it.
  const [locationIssue, setLocationIssue] = useState(null);
  const locatingRef = useRef(false);
  const awaitingSettingsRef = useRef(false);

  // @capacitor/geolocation shows the OS permission prompt in the native app and
  // falls back to navigator.geolocation in browsers (web/admin unaffected). See
  // utils/currentLocation for the permission / GPS-off / old-app-build handling.
  // `interactive` = the customer tapped the button: only then do we re-ask
  // after a refusal or offer Android's "turn on location" dialog.
  const handleUseCurrentLocation = async (interactive = true) => {
    if (locatingRef.current) return;
    locatingRef.current = true;
    setLocating(true);
    const pickAtStart = pickSeqRef.current;
    const native = Capacitor.isNativePlatform();
    try {
      const result = await locateCurrentPosition(
        { geolocation: Geolocation, native, pluginAvailable: !native || Capacitor.isPluginAvailable('Geolocation') },
        { interactive },
      );
      if (result.status === LocationStatus.OK) {
        setLocationIssue(null);
        if (pickSeqRef.current === pickAtStart) handleLocationSelect(result.coords.latitude, result.coords.longitude);
      } else {
        setLocationIssue({ status: result.status, interactive });
      }
    } finally {
      locatingRef.current = false;
      setLocating(false);
    }
  };

  const canOpenSettings = Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('AppSettings');
  const notice = locationIssue && locationNotice(locationIssue.status, {
    native: Capacitor.isNativePlatform(),
    platform: Capacitor.getPlatform(),
    canOpenSettings,
    interactive: locationIssue.interactive,
  });

  const handleNoticeAction = async () => {
    if (notice?.action === 'retry') { handleUseCurrentLocation(true); return; }
    if (notice?.action !== 'app_settings' && notice?.action !== 'location_settings') return;
    awaitingSettingsRef.current = true;
    try {
      if (notice.action === 'app_settings') await AppSettings.openAppSettings();
      else await AppSettings.openLocationSettings();
    } catch (_) {
      awaitingSettingsRef.current = false;
    }
  };

  // Back from Settings: try again quietly (never prompts). It succeeds only if
  // the customer actually turned the permission / location on.
  useEffect(() => {
    const retryAfterSettings = () => {
      if (!awaitingSettingsRef.current || document.visibilityState === 'hidden') return;
      awaitingSettingsRef.current = false;
      handleUseCurrentLocation(false);
    };
    document.addEventListener('visibilitychange', retryAfterSettings);
    const resumeListener = Capacitor.isNativePlatform() ? App.addListener('resume', retryAfterSettings) : null;
    return () => {
      document.removeEventListener('visibilitychange', retryAfterSettings);
      resumeListener?.then(handle => handle.remove()).catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async (query) => {
    if (!query || query.length < 3) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=in&limit=5&accept-language=en`,
        { headers: { 'User-Agent': 'GroceryApp/1.0' } }
      );
      setSearchResults(await res.json() || []);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  };

  const onSearchChange = (val) => {
    setSearchQuery(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => handleSearch(val), 500);
  };

  const selectSearchResult = (result) => {
    handleLocationSelect(parseFloat(result.lat), parseFloat(result.lon));
    setSearchQuery('');
    setSearchResults([]);
  };

  // Only auto-locate once the map step is actually reached — not while still
  // resolving whether this should be the apartment-first flow instead, and
  // not more than once (the map step can be revisited via "back").
  const autoLocateTriggeredRef = useRef(false);
  useEffect(() => {
    if (step !== 'map' || autoLocateTriggeredRef.current) return;
    autoLocateTriggeredRef.current = true;
    if (!initialAddress?.latitude) handleUseCurrentLocation(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // ── RESOLVING STEP — brief wait for the apartments fetch before deciding
  // whether to show the apartment list or the map, so the map (and its
  // GPS-permission prompt) never flashes for a tenant that turns out to use
  // the apartment-first flow. ──
  if (step === 'resolving') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#fff' }} data-testid="address-resolving-step">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--theme-primary)' }} />
      </div>
    );
  }

  // ── APARTMENT SELECT STEP — Scenario 1 entry point: searchable list of
  // configured apartments, no map involved at all. ──
  if (step === 'apartment') {
    const q = apartmentSearchQuery.trim().toLowerCase();
    const filteredApartments = q ? apartments.filter(a => a.name.toLowerCase().includes(q)) : apartments;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="apartment-select-step">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
          {apartmentFormMode ? (
            <button onClick={() => setStep('form')} style={{ padding: 4 }} data-testid="cancel-apartment-select-btn">
              <ArrowLeft size={20} color="var(--theme-primary)" />
            </button>
          ) : (
            <Home size={20} color="var(--theme-primary)" />
          )}
          <h2 style={{ fontWeight: 700, fontSize: 18 }}>Select Apartment</h2>
          {/* Reached via "Change" from an in-progress form (add or edit): dismissing
              this screen — via this X or the back arrow above — must only cancel the
              apartment change and return to that form, never close the whole editor.
              Only when this is the entry screen itself (nothing to go back to) does
              closing mean abandoning the address entirely. */}
          <button
            onClick={() => apartmentFormMode ? setStep('form') : onClose()}
            style={{ marginLeft: 'auto', padding: 4 }}
            data-testid="close-apartment-select-btn"
          >
            <X size={20} color="#9ca3af" />
          </button>
        </div>

        <div style={{ padding: '8px 16px', background: '#fff' }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} color="#9ca3af" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              value={apartmentSearchQuery}
              onChange={e => setApartmentSearchQuery(e.target.value)}
              placeholder="Search your apartment/society..."
              style={{ width: '100%', paddingLeft: 40, paddingRight: 16, paddingTop: 10, paddingBottom: 10, borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none' }}
              data-testid="apartment-search-input"
            />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
          {filteredApartments.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 14, padding: '32px 0' }}>
              {apartmentSearchQuery ? 'No matching apartments found.' : 'No apartments available.'}
            </p>
          ) : (
            filteredApartments.map(apartment => (
              <button
                key={apartment.id}
                onClick={() => selectApartment(apartment)}
                style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 12px', borderRadius: 12, border: '1px solid #f3f4f6', marginBottom: 8, background: '#fff', cursor: 'pointer' }}
                data-testid={`apartment-option-${apartment.id}`}
              >
                <Home size={18} color="var(--theme-primary)" style={{ marginTop: 2, flexShrink: 0 }} />
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{apartment.name}</p>
                  {apartment.address && (
                    <p style={{ fontSize: 12, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{apartment.address}</p>
                  )}
                </div>
              </button>
            ))
          )}

          {/* Tenant-configurable escape hatch for customers who don't live in
              any listed apartment — never filtered by search, always the last
              option. Continues with the original map-based flow, unmodified. */}
          {isOtherAddressEnabled && (
            <button
              onClick={selectOther}
              style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 12px', borderRadius: 12, border: '1px dashed #d1d5db', marginTop: filteredApartments.length > 0 ? 4 : 0, background: '#fafafa', cursor: 'pointer' }}
              data-testid="apartment-option-other"
            >
              <MapPin size={18} color="#6b7280" style={{ flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <p style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Other</p>
                <p style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>My address isn&apos;t in this list</p>
              </div>
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── FORM STEP ──
  if (step === 'form') {
    // Apartment-first variant: location is already known from the selected
    // apartment, so only ask for what's still needed (flat number + optional
    // landmark) instead of the full map-derived address form below.
    if (apartmentFormMode) {
      const selectedApartment = apartments.find(a => a.id === form.apartment_id);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="apartment-address-form-step">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
            <button onClick={() => setStep('apartment')} style={{ padding: 4 }} data-testid="back-to-apartment-list-btn">
              <ArrowLeft size={20} color="var(--theme-primary)" />
            </button>
            <h2 style={{ fontWeight: 700, fontSize: 18 }}>Complete Address</h2>
            <button onClick={onClose} style={{ marginLeft: 'auto', padding: 4 }}>
              <X size={20} color="#9ca3af" />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            <div style={{ marginBottom: 16 }}>
              <Label className="mb-2 block text-sm font-medium">Address Type</Label>
              <div style={{ display: 'flex', gap: 8 }}>
                {ADDRESS_TYPES.map(type => (
                  <button
                    key={type.value}
                    onClick={() => setForm(p => ({ ...p, label: type.value }))}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                      form.label === type.value
                        ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/10 text-[var(--theme-primary)]'
                        : 'border-gray-200'
                    }`}
                    data-testid={`address-type-${type.value.toLowerCase()}`}
                  >
                    <type.icon className="w-4 h-4" /> {type.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4 p-3 rounded-xl border border-[var(--theme-primary)]/20 bg-[var(--theme-primary)]/5 flex items-start justify-between gap-3" data-testid="selected-apartment-summary">
              <div className="flex items-start gap-2 min-w-0">
                <Home className="w-4 h-4 text-[var(--theme-primary)] shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{form.apartment_name || selectedApartment?.name || 'Selected apartment'}</p>
                  {selectedApartment?.address && <p className="text-xs text-gray-500 truncate mt-0.5">{selectedApartment.address}</p>}
                </div>
              </div>
              <button onClick={() => setStep('apartment')} className="text-xs font-medium text-[var(--theme-primary)] shrink-0" data-testid="change-apartment-btn">
                Change
              </button>
            </div>

            <div style={{ marginBottom: 12 }}>
              <Label className="text-sm">Flat / House Number *</Label>
              <Input
                value={form.flat_number}
                onChange={e => setForm(p => ({ ...p, flat_number: e.target.value }))}
                placeholder="e.g., A-203"
                className="mt-1"
                data-testid="address-flat-number-input"
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <Label className="text-sm">Landmark</Label>
              <Input
                value={form.landmark}
                onChange={e => setForm(p => ({ ...p, landmark: e.target.value }))}
                placeholder="Near..."
                className="mt-1"
                data-testid="address-landmark-input"
              />
            </div>
          </div>

          <div style={{ padding: 16, borderTop: '1px solid #f3f4f6' }}>
            <Button
              onClick={() => {
                if (!form.apartment_id) { toast.error('Please select an apartment.'); return; }
                if (!form.flat_number.trim()) { toast.error('Please enter your flat/house number.'); return; }
                onSave(form);
              }}
              className="w-full h-12 bg-[var(--theme-primary)] text-white rounded-xl text-base font-semibold"
              data-testid="save-address-btn"
            >
              {isEditing ? 'Update Address' : 'Save Address'}
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="address-form-step">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #f3f4f6' }}>
          <button onClick={() => setStep('map')} style={{ padding: 4 }} data-testid="back-to-map-btn">
            <ArrowLeft size={20} color="var(--theme-primary)" />
          </button>
          <h2 style={{ fontWeight: 700, fontSize: 18 }}>Complete Address</h2>
          <button onClick={onClose} style={{ marginLeft: 'auto', padding: 4 }}>
            <X size={20} color="#9ca3af" />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <Label className="mb-2 block text-sm font-medium">Address Type</Label>
            <div style={{ display: 'flex', gap: 8 }}>
              {ADDRESS_TYPES.map(type => (
                <button
                  key={type.value}
                  onClick={() => setForm(p => ({ ...p, label: type.value }))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                    form.label === type.value
                      ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/10 text-[var(--theme-primary)]'
                      : 'border-gray-200'
                  }`}
                  data-testid={`address-type-${type.value.toLowerCase()}`}
                >
                  <type.icon className="w-4 h-4" /> {type.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <Label className="text-sm">Full Address *</Label>
            <Input value={form.full_address} onChange={e => { userEditedRef.current.add('full_address'); setForm(p => ({ ...p, full_address: e.target.value })); }} placeholder="House no, Street, Area..." className="mt-1" data-testid="address-full-input" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <Label className="text-sm">Landmark</Label>
            <Input value={form.landmark} onChange={e => setForm(p => ({ ...p, landmark: e.target.value }))} placeholder="Near..." className="mt-1" data-testid="address-landmark-input" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <Label className="text-sm">City *</Label>
              <Input value={form.city} onChange={e => { userEditedRef.current.add('city'); setForm(p => ({ ...p, city: e.target.value })); }} placeholder="City" className="mt-1" data-testid="address-city-input" />
            </div>
            <div>
              <Label className="text-sm">Pincode{(apartmentGatingActive && !otherAddressMode) ? ' *' : ''}</Label>
              <Input value={form.pincode} onChange={e => { userEditedRef.current.add('pincode'); setForm(p => ({ ...p, pincode: e.target.value })); }} placeholder="Pincode" maxLength={6} className="mt-1" data-testid="address-pincode-input" />
            </div>
          </div>

          {apartmentGatingActive && !otherAddressMode && form.pincode && !pincodeServiceable && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700" data-testid="pincode-not-serviceable-warning">
              This delivery location is currently not serviceable.
            </div>
          )}

          {apartmentGatingActive && !otherAddressMode && pincodeServiceable && matchingApartments.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <Label className="text-sm">Apartment / Society *</Label>
              <Select
                value={form.apartment_id || ''}
                onValueChange={(value) => setForm(p => ({ ...p, apartment_id: value }))}
              >
                <SelectTrigger className="mt-1" data-testid="address-apartment-select">
                  <SelectValue placeholder="Select your apartment/society" />
                </SelectTrigger>
                {/* MapAddressPicker is always mounted inside a z-[9999] full-screen
                    overlay (see AddressSelector/CustomerAddresses/CustomerCart) —
                    the default z-50 popover would render behind that opaque overlay
                    and never be visible, even though it does technically open. */}
                <SelectContent className="z-[10000]">
                  {matchingApartments.map(apartment => (
                    <SelectItem key={apartment.id} value={apartment.id}>{apartment.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {apartmentGatingActive && !otherAddressMode && form.apartment_id && (
            <div style={{ marginBottom: 12 }}>
              <Label className="text-sm">Flat / House Number *</Label>
              <Input
                value={form.flat_number}
                onChange={e => setForm(p => ({ ...p, flat_number: e.target.value }))}
                placeholder="e.g., A-203"
                className="mt-1"
                data-testid="address-flat-number-input"
              />
            </div>
          )}

          <div>
            <Label className="text-sm">State</Label>
            <Input value={form.state} onChange={e => { userEditedRef.current.add('state'); setForm(p => ({ ...p, state: e.target.value })); }} placeholder="State" className="mt-1" data-testid="address-state-input" />
          </div>
        </div>

        <div style={{ padding: 16, borderTop: '1px solid #f3f4f6' }}>
          <Button
            onClick={() => {
              if (!form.full_address || !form.city) { toast.error('Please fill address and city'); return; }
              if (apartmentGatingActive && !otherAddressMode) {
                if (!pincodeServiceable) { toast.error('This delivery location is currently not serviceable.'); return; }
                if (!form.apartment_id) { toast.error('Please select a serviceable apartment for this address.'); return; }
                if (!form.flat_number.trim()) { toast.error('Please enter your flat/house number.'); return; }
              }
              onSave(form);
            }}
            className="w-full h-12 bg-[var(--theme-primary)] text-white rounded-xl text-base font-semibold"
            data-testid="save-address-btn"
          >
            {isEditing ? 'Update Address' : 'Save Address'}
          </Button>
        </div>
      </div>
    );
  }

  // ── MAP STEP ──
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }} data-testid="map-picker-step">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #f3f4f6', background: '#fff' }}>
        <MapPin size={20} color="var(--theme-primary)" />
        <h2 style={{ fontWeight: 700, fontSize: 18 }}>Select Location</h2>
        <button onClick={onClose} style={{ marginLeft: 'auto', padding: 4 }} data-testid="close-map-btn">
          <X size={20} color="#9ca3af" />
        </button>
      </div>

      {/* Search Bar — z-index must be above the map wrapper to show search results over the map */}
      <div style={{ padding: '8px 16px', background: '#fff', position: 'relative', zIndex: 1000 }}>
        <div style={{ position: 'relative' }}>
          <Search size={16} color="#9ca3af" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Search for area, street..."
            style={{ width: '100%', paddingLeft: 40, paddingRight: 16, paddingTop: 10, paddingBottom: 10, borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none' }}
            data-testid="map-search-input"
          />
          {searching && <Loader2 size={16} className="animate-spin" style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />}
        </div>
        {searchResults.length > 0 && (
          <div style={{ position: 'absolute', left: 16, right: 16, top: '100%', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto', zIndex: 1001 }}>
            {searchResults.map((r, i) => (
              <button key={i} onClick={() => selectSearchResult(r)} style={{ width: '100%', textAlign: 'left', padding: '12px 16px', fontSize: 13, borderBottom: '1px solid #f9fafb', cursor: 'pointer', background: '#fff', border: 'none' }} data-testid={`search-result-${i}`}>
                <span style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{r.display_name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Why current location isn't available + the one useful next step. In the
          normal flow (not over the map) so it never covers the map controls or
          the Confirm button; search results above still overlay it. */}
      {notice && (
        <div
          role="status"
          style={{ margin: '0 16px 8px', padding: '10px 12px', borderRadius: 12, background: '#FFFBEB', border: '1px solid #FDE68A', display: 'flex', alignItems: 'flex-start', gap: 10 }}
          data-testid="location-notice"
          data-status={locationIssue.status}
        >
          <LocateOff size={18} color="#B45309" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: '#92400E', lineHeight: 1.35 }}>{notice.title}</p>
            <p style={{ fontSize: 12, color: '#78350F', lineHeight: 1.4, marginTop: 2 }}>{notice.message}</p>
            {notice.action && (
              <button
                onClick={handleNoticeAction}
                disabled={locating}
                style={{ marginTop: 8, minHeight: 32, padding: '6px 14px', borderRadius: 8, border: 'none', background: 'var(--theme-primary)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: locating ? 0.6 : 1 }}
                data-testid="location-notice-action"
              >
                {locating ? 'Locating…' : notice.actionLabel}
              </button>
            )}
          </div>
          <button onClick={() => setLocationIssue(null)} aria-label="Dismiss" style={{ padding: 4, margin: -4, flexShrink: 0 }} data-testid="location-notice-dismiss">
            <X size={16} color="#92400E" />
          </button>
        </div>
      )}

      {/* Map + Overlay Controls: controls are OUTSIDE MapContainer to avoid Leaflet z-index stacking */}
      <div style={{ flex: 1, position: 'relative', minHeight: 0, overflow: 'hidden', zIndex: 1 }}>
        <MapContainer
          center={position || DEFAULT_CENTER}
          zoom={position ? 17 : DEFAULT_ZOOM}
          style={{ width: '100%', height: '100%' }}
          zoomControl={false}
          attributionControl={false}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapClickHandler onLocationSelect={handleLocationSelect} />
          {position && <Marker position={position} />}
          {position && <FlyTo position={position} zoom={17} />}
          <MapRefExporter onMap={setMapRef} />
        </MapContainer>

        {/* Zoom Controls — absolute overlay on the map wrapper, OUTSIDE the Leaflet container */}
        <div style={{ position: 'absolute', right: 12, top: 12, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 4, pointerEvents: 'none' }}>
          <button
            onClick={() => mapRef?.zoomIn()}
            style={{ width: 40, height: 40, borderRadius: 10, background: '#fff', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', cursor: 'pointer', pointerEvents: 'auto' }}
            data-testid="zoom-in-btn"
          >
            <ZoomIn size={18} color="#374151" />
          </button>
          <button
            onClick={() => mapRef?.zoomOut()}
            style={{ width: 40, height: 40, borderRadius: 10, background: '#fff', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.15)', cursor: 'pointer', pointerEvents: 'auto' }}
            data-testid="zoom-out-btn"
          >
            <ZoomOut size={18} color="#374151" />
          </button>
        </div>

        {/* Current Location Button */}
        <button
          onClick={() => handleUseCurrentLocation(true)}
          disabled={locating}
          style={{ position: 'absolute', right: 12, bottom: 16, zIndex: 1000, width: 44, height: 44, borderRadius: '50%', background: '#fff', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', cursor: 'pointer' }}
          data-testid="current-location-btn"
        >
          {locating
            ? <Loader2 size={20} className="animate-spin" color="var(--theme-primary)" />
            : <Navigation size={20} color="var(--theme-primary)" />
          }
        </button>

        {/* Reversing indicator */}
        {reversing && (
          <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 12, padding: '6px 14px', borderRadius: 20 }}>
            Fetching address...
          </div>
        )}
      </div>

      {/* Confirm Button — always visible at bottom, outside map area */}
      <div style={{ padding: 16, borderTop: '1px solid #f3f4f6', background: '#fff' }}>
        {position && form.full_address && (
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '0 4px' }}>{form.full_address}</p>
        )}
        <Button
          onClick={() => {
            if (!position) { toast.error('Please select a location on the map'); return; }
            if (locationOnly) {
              onSave({ full_address: form.full_address, latitude: form.latitude, longitude: form.longitude });
              return;
            }
            setStep('form');
          }}
          disabled={!position}
          className="w-full h-12 bg-[var(--theme-primary)] text-white rounded-xl text-base font-semibold"
          data-testid="confirm-location-btn"
        >
          Confirm Location
        </Button>
      </div>
    </div>
  );
};

export default MapAddressPicker;
